import {Component, OnInit} from '@angular/core';
import {Handler} from "src/app/common/handler";
import {ConnectionType, DataType} from "@flowmaps-com/flowmaps-typescriptmodels";
import {BaseMeasurementChartComponent, MeasurementDataset} from "../base-measurement-chart";
import {ChartOptionType} from "../../dashboard/dashboard.types";
import {scatterExclusiveTooltipPlugin, syncScatterYAxisPlugin} from "../bar-chart/bar-chart.component";
import {AppContext} from "../../../app-context";
import {TranslateDirective} from "../../../common/utils/translate.directive";
import {
    allowOffPeakSplit,
    calculateElectricityBalance,
    electricityOutsideSelectionSeries,
    mainMeterSeries,
    selectChargePointView,
    showChargePointOverlay,
    showChargePointsFromBaseline,
    showChargePointShareGroup,
    showChargePointsWithSubMeters,
    showMainMeterSummary,
    subtractChargePointShare
} from "./electricity-balance";
import {EntityType} from '../../../handlers/entity';
import {isCalculatedResidual} from '../../refdata/meters/submeter-role.utils';

@Component({
  selector: 'app-electricity-chart',
  templateUrl: './electricity-chart.component.html',
  styleUrls: ['./electricity-chart.component.scss']
})
@Handler()
export class ElectricityChartComponent extends BaseMeasurementChartComponent implements OnInit {
  chartOptionType: ChartOptionType = ChartOptionType.Electricity;
  mainConsumptionType: DataType = DataType.electricityConsumption;
  mainConnectionType: ConnectionType = ConnectionType.Electricity;
  connectionTypes: ConnectionType[] = [ConnectionType.Electricity];
  get downloadConnectionTypes(): ConnectionType[] {
    return this.showChargePoints()
        ? [ConnectionType.Electricity, ConnectionType.ChargePoint]
        : [ConnectionType.Electricity];
  }
  feedInPowerTypes: DataType[] = [DataType.electricityFeedInPower];
  contractedCapacityRequiredForPower: boolean = true;

  openModal() {
    super.openModal(ElectricityChartComponent);
  }

  productionDataTypes = (): DataType[] => this.showConsumption() && this.showFeedIn()
      ? [DataType.electricityFeedIn, DataType.electricityFeedInOffPeak] : [];

  measurementTypes = (): DataType[] => {
    // The main-meter data is also loaded for an EV-only view so the charge-point
    // percentage can still be calculated without rendering the main-meter series.
    const needsMainMeterTotal = this.showConsumption() || this.showChargePoints();
    let baseMeasurements = needsMainMeterTotal
        ? [DataType.electricityConsumption, DataType.electricityConsumptionOffPeak] : [];
    if (this.options.showReactive && this.showConsumption()) {
      baseMeasurements = baseMeasurements.concat(DataType.electricityConsumptionReactive,
          DataType.electricityConsumptionReactiveOffPeak);
    }
    if (this.options.showGrossProduction) {
      baseMeasurements = baseMeasurements.concat([DataType.electricityGrossProduction]);
      if (this.options.showReactive) {
        baseMeasurements = baseMeasurements.concat([DataType.electricityGrossProductionReactive]);
      }
    }
    if (this.showConsumption() && this.groupByEntityIdEnabled()) {
      baseMeasurements = baseMeasurements.concat([DataType.electricityIntermediateConsumption]);
      if (this.options.showReactive) {
        baseMeasurements = baseMeasurements.concat(DataType.electricityIntermediateConsumptionReactive);
      }
    }
    if (this.showChargePoints()) {
      baseMeasurements = baseMeasurements.concat([DataType.chargePointConsumption]);
    }
    return [...new Set(baseMeasurements)];
  };

  measurementIntermediateLink = (): Map<DataType, DataType[]> => new Map<DataType, DataType[]>()
      .set(DataType.electricityConsumption, [DataType.electricityIntermediateConsumption,
        DataType.chargePointConsumption])
      .set(DataType.electricityConsumptionOffPeak, [DataType.electricityIntermediateConsumption,
        DataType.chargePointConsumption]);

  consumptionProductionLink = (): Map<DataType, DataType> => new Map<DataType, DataType>()
      .set(DataType.electricityConsumption, DataType.electricityFeedIn)
      .set(DataType.electricityConsumptionOffPeak, DataType.electricityFeedInOffPeak);

  measurementTypesMapped = (): {[key: string]: DataType[]} => {
    const measurementTypes = {
      [DataType.electricityConsumption]: [DataType.electricityConsumption, DataType.electricityConsumptionOffPeak],
      [DataType.electricityIntermediateConsumption]: [DataType.electricityIntermediateConsumption],
      [DataType.electricityGrossProduction]: [DataType.electricityGrossProduction],
      [DataType.chargePointConsumption]: [DataType.chargePointConsumption]
    };
    if (this.options.showReactive) {
      measurementTypes[DataType.electricityConsumptionReactive] = [DataType.electricityConsumptionReactive, DataType.electricityConsumptionReactiveOffPeak];
    }
    if (this.options.showGrossProduction) {
      measurementTypes[DataType.electricityGrossProduction] = [DataType.electricityGrossProduction];
      if (this.options.showReactive) {
        measurementTypes[DataType.electricityGrossProductionReactive] = [DataType.electricityGrossProductionReactive];
      }
    }
    return measurementTypes;
  }

  productionDataTypesMapped = (): {[key: string]: DataType[]} => ({
    [DataType.electricityFeedIn]: [DataType.electricityFeedIn, DataType.electricityFeedInOffPeak],
  });

  override powerDataTypes(connectionType: ConnectionType): DataType[] {
    return connectionType === ConnectionType.Electricity
      ? [DataType.electricityPower, DataType.electricityFeedInPower]
      : super.powerDataTypes(connectionType);
  }

  splitOffPeakChange(splitOffPeak: boolean) {
    this.options.splitOffPeak = splitOffPeak && this.splitOffPeakEnabled();
    this.refreshData(true);
  }

  splitOffPeakEnabled = (): boolean => allowOffPeakSplit(
    this.showChargePoints(), this.groupByEntityIdEnabled());

  showChargePoints = (): boolean => showChargePointsWithSubMeters(
    !!this.options?.showChargePoints, this.groupByEntityIdEnabled());

  override groupByEntityIdChange(groupByEntityId: boolean) {
    if (groupByEntityId) {
      this.options.showConsumption = true;
      this.options.showChargePoints = true;
      this.options.splitOffPeak = false;
    }
    super.groupByEntityIdChange(groupByEntityId);
  }

  showConsumptionChange(showConsumption: boolean) {
    this.options.showConsumption = showConsumption;
    this.options.nettedConsumption = showConsumption ? this.options.nettedConsumption : false;
    this.refreshData(true);
  }

  showChargePointsChange(showChargePoints: boolean) {
    const selection = selectChargePointView(this.showConsumption(), this.options.splitOffPeak, showChargePoints);
    this.options.showConsumption = selection.showConsumption;
    this.options.showChargePoints = selection.showChargePoints;
    this.options.splitOffPeak = selection.splitOffPeak;
    this.refreshData(true);
  }

  hasChargePointData = (): boolean => this.hasDataOfTypes([
    DataType.chargePointConsumption, DataType.chargePointIntermediateConsumption
  ]);

  hasFeedInData = (): boolean => this.hasDataOfTypes([
    DataType.electricityFeedIn, DataType.electricityFeedInOffPeak
  ]);

  hasContractedFeedInPower = (): boolean => this.feedInPowerTypes
      .some(powerType => !!this.contractedCapacity(this.mainConnectionType, powerType));

  protected override measurementQueryFeatures() {
    return {...super.measurementQueryFeatures(), includeElectricityConnectionContext: true};
  }

  protected override transformMeasurementDatasets(datasets: MeasurementDataset[], isPrevious: boolean,
                                                  isEstimated: boolean): MeasurementDataset[] {
    const transformed = super.transformMeasurementDatasets(datasets, isPrevious, isEstimated);

    const chargePointDatasets = transformed.filter(d => d.measurementType === DataType.chargePointConsumption);
    const electricityDatasets = transformed.filter(d => [DataType.electricityConsumption,
      DataType.electricityConsumptionOffPeak].includes(d.measurementType));
    if (!electricityDatasets.length) {
      return this.applySourceLayerOrder(transformed);
    }

    const {
      chargePointTotals,
      electricityTotals,
      allocatedChargePointTotals: displayedChargePointTotals
    } = calculateElectricityBalance(
      electricityDatasets.map(d => mainMeterSeries(
        d.dataset.data.map(Number), d.dataset.tooltip?.mainMeterTotalData?.map(Number))),
      chargePointDatasets.map(d => d.dataset.data.map(Number))
    );
    const renderChargePointsAsSegments = showChargePointOverlay(
      this.showConsumption(), this.showChargePoints(), this.groupByEntityIdEnabled());
    const electricitySegmentData = subtractChargePointShare(
      electricityDatasets.map(dataset => dataset.dataset.data.map(Number)), displayedChargePointTotals);
    const separatedDatasets = transformed.map(source => {
      if ([DataType.electricityConsumption, DataType.electricityConsumptionOffPeak]
          .includes(source.measurementType)) {
        // Keep the main-meter series available while the visible segments are prepared.
        if (!showMainMeterSummary(this.groupByEntityIdEnabled())) {
          return source;
        }
        const electricityDatasetIndex = electricityDatasets.indexOf(source);
        return <MeasurementDataset>{
          ...source,
          dataset: {
            ...source.dataset,
            ...(renderChargePointsAsSegments && electricityDatasetIndex >= 0
              ? {data: electricitySegmentData[electricityDatasetIndex]} : {}),
            tooltip: {
              ...source.dataset.tooltip,
              ...(renderChargePointsAsSegments ? {data: electricityTotals} : {})
            }
          }
        };
      }
      if (source.measurementType === DataType.chargePointConsumption) {
        const periodTooltipStack = source.dataset.tooltip?.stack || source.dataset.stack;
        const renderAsOverlay = renderChargePointsAsSegments;
        const showChargingShareGroup = showChargePointShareGroup(
          this.showConsumption(), this.showChargePoints(), this.groupByEntityIdEnabled(),
          !!this.options.compare?.enabled);
        const renderFromSubMeterBaseline = showChargePointsFromBaseline(
          this.showChargePoints(), this.groupByEntityIdEnabled());
        const renderFromBaseline = renderAsOverlay;
        const result: MeasurementDataset = {
          ...source,
          dataset: <any>{
            ...source.dataset,
            tooltip: {
              ...source.dataset.tooltip,
              ...(showChargingShareGroup ? {
                stack: TranslateDirective.getTranslation("Charging share", true),
                // Keep charging information below the other period groups,
                // independently of the dataset drawing order.
                groupOrder: 100,
                ...(renderFromSubMeterBaseline ? {order: 350} : {})
              } : renderAsOverlay ? {
                // During a comparison, keep charging underneath the same
                // period heading as the electricity bar it belongs to.
                stack: periodTooltipStack
              } : renderFromSubMeterBaseline ? {
                // In a sub-meter split, charging is one of the allocated
                // branches and therefore belongs in the regular period list.
                stack: periodTooltipStack,
                order: 350
              } : {})
            },
            // Charging is a segment of its own period bar. This gives the
            // current and comparison years the same side-by-side composition
            // as the existing production view, while preserving the total.
            ...(renderFromBaseline ? {stack: source.dataset.stack} : {}),
            data: source.dataset.data.map((value, index) => {
              const total = chargePointTotals[index];
              return total > 0 ? Number(value || 0) * displayedChargePointTotals[index] / total : 0;
            })
          }
        };
        result.dataset.tooltip.formatter = (context, value) => {
          const index = context["dataIndex"];
          const percentage = electricityTotals[index] > 0
            ? Math.round(displayedChargePointTotals[index] / electricityTotals[index] * 1000) / 10 : 0;
          return `${this.chartUtils.defaultTooltipFormatter(value, DataType.chargePointConsumption)} `
            + `(${percentage}% ${TranslateDirective.getTranslation("of total electricity", true)})`;
        };
        return result;
      }
      return source;
    });
    return this.applySourceLayerOrder(separatedDatasets);
  }

  protected override filterMeasurementDatasetsForDisplay(datasets: MeasurementDataset[]): MeasurementDataset[] {
    const selectedMeterIds = this.selectedPhysicalMeterIds();
    const displayed = datasets.filter(dataset => this.isDatasetVisible(dataset))
      .filter(dataset => !selectedMeterIds.size || !dataset.entityId || selectedMeterIds.has(dataset.entityId)
        || dataset.dataset.tooltip?.calculatedResidual === true);
    if (!selectedMeterIds.size) return displayed;

    const allocated = datasets.filter(dataset => dataset.entityId
      && this.isAllocatedElectricityDataset(dataset));
    const selected = allocated.filter(dataset => selectedMeterIds.has(dataset.entityId));
    const selectedConnectionIds = new Set(selected.map(dataset => dataset.connectionId)
      .filter((connectionId): connectionId is string => !!connectionId));
    if (!selected.length || selected.some(dataset => dataset.connectionTotalAuthorized !== true)
        || selectedConnectionIds.size !== 1) {
      return displayed;
    }

    const connectionId = [...selectedConnectionIds][0];
    const connectionAllocated = allocated.filter(dataset => dataset.connectionId === connectionId);
    const outsideSelectionData = electricityOutsideSelectionSeries(
      connectionAllocated.map(dataset => dataset.dataset.data.map(Number)),
      selected.map(dataset => dataset.dataset.data.map(Number)));
    if (!outsideSelectionData.some(value => value > 0)) return displayed;

    return [...displayed, this.outsideSelectionDataset(
      selected[0], connectionId, outsideSelectionData)];
  }

  private isAllocatedElectricityDataset(dataset: MeasurementDataset): boolean {
    return [
      DataType.electricityIntermediateConsumption,
      DataType.chargePointConsumption,
      DataType.chargePointIntermediateConsumption
    ].includes(dataset.measurementType);
  }

  private outsideSelectionDataset(template: MeasurementDataset, connectionId: string,
                                  data: number[]): MeasurementDataset {
    const label = TranslateDirective.getTranslation('Consumption outside selection', true) as string;
    const estimated = template.dataset.tooltip?.estimated === true;
    const color = '#a8b3c2';
    return {
      measurementType: DataType.electricityIntermediateConsumption,
      connectionId,
      connectionTotalAuthorized: true,
      estimated,
      dataset: {
        ...template.dataset,
        label: estimated
          ? `${label} (${TranslateDirective.getTranslation('estimated', true)})` : label,
        data,
        order: 9000,
        backgroundColor: estimated ? '#ffffff' : color,
        borderColor: color,
        borderWidth: estimated ? 1 : 0,
        tooltip: {
          ...template.dataset.tooltip,
          order: 900,
          data,
          hasData: data.map(value => value > 0),
          formatter: (context, value) => this.chartUtils.defaultTooltipFormatter(
            value, DataType.electricityIntermediateConsumption)
        }
      }
    };
  }

  private selectedPhysicalMeterIds(): Set<string> {
    const sources = this.filters?.allSelectedSources?.length
      ? this.filters.allSelectedSources : this.filters?.cleanedSources || this.filters?.sources || [];
    return new Set(sources
      .filter(source => source.type === EntityType.meter && !isCalculatedResidual(source.source?.meter))
      .map(source => source.id));
  }

  private isDatasetVisible(source: MeasurementDataset): boolean {
    if ([DataType.electricityConsumption, DataType.electricityConsumptionOffPeak]
        .includes(source.measurementType)) {
      return this.showConsumption();
    }
    if (source.measurementType === DataType.chargePointConsumption) {
      return this.showChargePoints();
    }
    return true;
  }

  private applySourceLayerOrder(datasets: MeasurementDataset[]): MeasurementDataset[] {
    return datasets.map(source => {
      const layerOrder = this.sourceLayerOrder(source.measurementType);
      const tooltipOrder = this.sourceTooltipOrder(source.measurementType);
      if (layerOrder == null && tooltipOrder == null) return source;

      const defaultOrder = AppContext.indexOfDataType(source.measurementType);
      const estimated = !!source.dataset.tooltip?.estimated;
      const currentBaseOrder = estimated ? defaultOrder * 1000 : defaultOrder;
      const entityOffset = Number(source.dataset.order) - currentBaseOrder;
      return {
        ...source,
        dataset: {
          ...source.dataset,
          ...(layerOrder == null ? {} : {
            order: (estimated ? layerOrder * 1000 : layerOrder)
                + (Number.isFinite(entityOffset) ? entityOffset : 0)
          }),
          tooltip: {
            ...source.dataset.tooltip,
            ...(tooltipOrder == null ? {} : {order: tooltipOrder})
          }
        }
      };
    });
  }

  private sourceTooltipOrder(measurementType: DataType): number | null {
    switch (measurementType) {
      case DataType.electricityConsumption:
      case DataType.electricityConsumptionOffPeak:
      case DataType.electricityConsumptionReactive:
      case DataType.electricityConsumptionReactiveOffPeak:
        return 100;
      case DataType.electricityFeedIn:
      case DataType.electricityFeedInOffPeak:
      case DataType.electricityFeedInReactive:
      case DataType.electricityFeedInReactiveOffPeak:
        return 200;
      case DataType.electricityGrossProduction:
      case DataType.electricityGrossProductionReactive:
        return 300;
      case DataType.chargePointConsumption:
      case DataType.chargePointIntermediateConsumption:
        return 400;
      default:
        return null;
    }
  }

  private sourceLayerOrder(measurementType: DataType): number | null {
    switch (measurementType) {
      case DataType.electricityGrossProduction:
      case DataType.electricityGrossProductionReactive:
        return 100;
      case DataType.chargePointConsumption:
      case DataType.chargePointIntermediateConsumption:
        return 200;
      case DataType.electricityIntermediateConsumption:
      case DataType.electricityIntermediateConsumptionReactive:
        return 300;
      case DataType.electricityConsumption:
        return 400;
      case DataType.electricityConsumptionOffPeak:
        return 401;
      case DataType.electricityConsumptionReactive:
        return 402;
      case DataType.electricityConsumptionReactiveOffPeak:
        return 403;
      default:
        return null;
    }
  }

  showFeedInChange(showFeedIn: boolean) {
    this.options.showFeedIn = showFeedIn;
    this.refreshData(true);
  }

  nettedConsumptionChange(nettedConsumption: boolean) {
    this.options.nettedConsumption = nettedConsumption;
    this.refreshData(true);
  }

  showReactiveChange(showReactive: boolean) {
    this.options.showReactive = showReactive;
    this.refreshData(true);
  }

  protected getYAxis() {
    return this.options.splitOffPeak ? "y1" : "y1Aggregated";
  }

  plugins = [scatterExclusiveTooltipPlugin, syncScatterYAxisPlugin];
  protected readonly AppContext = AppContext;
}
